import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRouteSnapshot, CanActivate, Router, RouterStateSnapshot, UrlTree } from '@angular/router';
import { ModalComponent } from '../components/modal/modal.component';
import { User } from '../models/user';
import { GUEST_ACTION_NOT_AVAILABLE_MSG, isGuestUser } from '../models/guest-user';
import { CustomerService } from '../services/customer.service';
import { NavigationService } from '../services/navigation.service';
import { UserService } from '../services/user.service';
import { CanActivateAuthGuard } from './can-activate-auth-guard';

@Injectable({
	providedIn: 'root'
})
export class PermissionGuard implements CanActivate {
	public currentDriver: any;
	public selectedCustomer: any;
	public user: User;

	constructor(private _authGuard: CanActivateAuthGuard,
				private _userService: UserService,
				private _dialog: MatDialog,
				private _customerService: CustomerService,
				private _navigationService: NavigationService,
				private _router: Router) {
	}

	canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Promise<boolean | UrlTree> {
		return this._authGuard.canActivate(route, state).then((auth: boolean): Promise<boolean | UrlTree> => {
			this.user = this._userService.getUser();
			if (this.user && this.user.type === 'worker' && this.user.driverId) {
				this.currentDriver = this.user.associatedDrivers.find((ad) => ad.id === this.user.driverId);
			}
			if (!auth) {
				return Promise.resolve(false);
			} else {
				this.selectedCustomer = this._customerService.getSelectedCustomer();
				// XD-62456: a guest is admitted only to the guest ordering flow (place order, its confirmation and the
				// tracking of what it placed). The account pages below are refused for a guest by identity, so a
				// drifted guest permission row cannot open them by URL.
				const guest = isGuestUser(this.user);
				switch (true) {
					case (state.url.includes('/home')):
					case (state.url.includes('/register')):
					case (state.url.includes('/track-order') && ((!this.user?.token && this.user?.globalSettings?.trackingOptions?.isActive) || (this.user?.type === 'inet' && this.selectedCustomer?.billingStatus !== 'CREDIT_HOLD'))):
					case (state.url.includes('/reset')):
					case (state.url.includes('/custom-page')):
					case (state.url.includes('/attachments')):
					case (state.url.includes('/track-parcel') && this.user?.type === 'inet' && !guest && (this.user?.inetUser?.allowTrackOrder || this.user?.licenses?.xRouteEnabled) && this.selectedCustomer?.billingStatus !== 'CREDIT_HOLD'):
					case (state.url.includes('/place-order') && this.user?.type === 'inet' && this.user?.inetUser?.allowPlaceOrders && this.selectedCustomer?.billingStatus !== 'CREDIT_HOLD'):
					case (state.url.includes('/order-confirmation') && this.user?.type === 'inet' && this.selectedCustomer?.billingStatus !== 'CREDIT_HOLD'):
					case (state.url.includes('/reports') && this.user?.type === 'inet' && !guest && this.user?.inetUser?.viewAccountReports && this.selectedCustomer?.billingStatus !== 'CREDIT_HOLD'):
					case (state.url.includes('/route-tracking') && this.user?.type === 'inet' && !guest && this.user?.inetUser?.viewRouteStops && this.user?.licenses?.xRouteEnabled && this.selectedCustomer?.billingStatus !== 'CREDIT_HOLD'):
					case (state.url.includes('/address-book') && this.user?.type === 'inet' && !guest && this.user?.inetUser?.allowAddressBookAccess && this.selectedCustomer?.billingStatus !== 'CREDIT_HOLD'):
					case (state.url.includes('/order-templates') && this.user?.type === 'inet' && !guest && this.user?.allowRecentTemplates && !this.blockedOrderForm(route, state) && this.selectedCustomer?.billingStatus !== 'CREDIT_HOLD'):
					case (state.url.includes('/invoices') && this.user?.type === 'inet' && !guest && this.user?.globalSettings?.displayInvoices && this.user?.inetUser?.viewInvoices):
					case (state.url.includes('/user-profile') && (this.user?.type === 'inet' && !guest && this.user?.canViewOwnProfile && this.selectedCustomer?.billingStatus !== 'CREDIT_HOLD') || this.user?.type === 'worker'):
					case (state.url.includes('/work') && this.user?.type === 'worker' && this.currentDriver?.canViewManifests):
					case (state.url.includes('/settlements') && this.user?.type === 'worker'):
					case (state.url.includes('/estimated-settlements') && this.user?.type === 'worker'):
						return Promise.resolve(true);
					default:
						// XD-62456: a guest refused an account page (typed URL, bookmark, back button) is told why and
						// sent to its landing page in one router operation. A bare `false` cancelled the navigation
						// silently, and on a fresh load of that URL left an empty content area.
						if (guest) {
							this._dialog.open(ModalComponent, {
								data: {title: 'Not available', message: GUEST_ACTION_NOT_AVAILABLE_MSG, showCancel: false}
							});
							return Promise.resolve(this._router.createUrlTree([this._navigationService.resolveDefaultLanding(this.user)]));
						}
						if (state.url.includes('/route-tracking') && this.user?.type === 'inet' && (!this.user?.inetUser?.viewRouteStops || !this.user?.licenses?.xRouteEnabled)) {
							this._dialog.open(ModalComponent, {
								data: {
									title: 'Alert',
									message: 'You currently do not have permission to view routes. Please contact customer service for assistance in enabling this feature.',
									showCancel: false
								}
							});
						}
						// Place Order was the historical default landing page, so a disabled user
						// hitting it (typed URL, in-app link, or back button) must be sent to their
						// first accessible page instead of dead-ending on a cancelled navigation.
						// Returning a UrlTree keeps the cancel and the redirect a single router
						// operation, so the address bar cannot end up out of step with the rendered
						// route when the navigation came from the back button.
						if (this.blockedOrderForm(route, state)) {
							return Promise.resolve(this._router.createUrlTree([this._navigationService.resolveDefaultLanding(this.user)]));
						}
						return Promise.resolve(false);
				}
			}
		});
	}

	/**
	 * True when the requested URL renders the order-entry form but the user is not
	 * allowed to place orders. `/order-templates/template` lazy-loads the same
	 * OrderEntryComponent as `/place-order` and only behaves as a template editor when
	 * `isTemplate=true`, so its plain-order mode has to honour the permission too.
	 */
	private blockedOrderForm(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): boolean {
		if (this.user?.type !== 'inet' || this.user?.inetUser?.allowPlaceOrders) {
			return false;
		}
		return state.url.includes('/place-order') ||
			(state.url.includes('/order-templates/template') && !this.isTemplateMode(route));
	}

	/**
	 * Mirrors how OrderEntryComponent reads the flag (order-entry.component.ts:1155):
	 * anything other than an explicit `true` renders the ordinary order form.
	 */
	private isTemplateMode(route: ActivatedRouteSnapshot): boolean {
		return `${route?.queryParams?.['isTemplate']}`.toLowerCase() === 'true';
	}
}

